import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import EmailNav from "./EmailNav";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ChevronLeft, ChevronRight, Send, Eye, Save, Calendar as CalIcon, Sparkles } from "lucide-react";
import { toast } from "sonner";

const BLANK = {
  name: "", subject: "", preview_text: "", body_html: "", brand: "rrdc",
  audience: { list_ids: [], brands: [], inquiry_types: [], statuses: [], tags: [], include_unsubscribed: false },
  from_name: "Marissa", from_email: "", reply_to: "",
};

const INQUIRY_TYPES = ["rental", "birthday_party", "event", "picnic"];
const LEAD_STATUSES = ["new", "contacted", "tour_scheduled", "contract_sent", "contract_signed", "paid", "booked"];

export default function CampaignBuilderPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [campaign, setCampaign] = useState(BLANK);
  const [campaignId, setCampaignId] = useState(id || null);
  const [audiencePreview, setAudiencePreview] = useState(null);
  const [lists, setLists] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [showTestDialog, setShowTestDialog] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [showSchedule, setShowSchedule] = useState(false);
  const [scheduleAt, setScheduleAt] = useState("");
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);

  // Load existing or load deps
  useEffect(() => {
    api.get("/email/lists").then(({ data }) => setLists(data.lists || []));
    api.get("/email/templates").then(({ data }) => setTemplates(data.templates || []));
    if (id) {
      api.get(`/email/campaigns/${id}`).then(({ data }) => setCampaign({ ...BLANK, ...data.campaign, audience: { ...BLANK.audience, ...(data.campaign.audience || {}) } }));
    }
  }, [id]);

  const ensureSaved = async () => {
    // Save (create or patch) and return id
    if (campaignId) {
      await api.patch(`/email/campaigns/${campaignId}`, campaign);
      return campaignId;
    }
    if (!campaign.name) { toast.error("Name your campaign first"); throw new Error("no name"); }
    const { data } = await api.post("/email/campaigns", campaign);
    setCampaignId(data.campaign.id);
    return data.campaign.id;
  };

  const previewAudience = async () => {
    try {
      const cid = await ensureSaved();
      const { data } = await api.post(`/email/campaigns/${cid}/audience-preview`);
      setAudiencePreview(data);
    } catch { /* toast already shown */ }
  };

  const saveDraft = async () => {
    try { await ensureSaved(); toast.success("Draft saved"); }
    catch { toast.error("Save failed"); }
  };

  const sendNow = async () => {
    if (!window.confirm(`Send this campaign to ${audiencePreview?.total ?? "?"} contacts now?`)) return;
    try {
      const cid = await ensureSaved();
      await api.post(`/email/campaigns/${cid}/send`);
      toast.success("Campaign queued for sending");
      navigate(`/email/campaigns/${cid}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
  };

  const sendTest = async () => {
    if (!testEmail) return toast.error("Email required");
    try {
      const cid = await ensureSaved();
      await api.post(`/email/campaigns/${cid}/test`, { email: testEmail });
      toast.success(`Test sent to ${testEmail}`);
      setShowTestDialog(false); setTestEmail("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
  };

  const schedule = async () => {
    if (!scheduleAt) return toast.error("Pick a date/time");
    try {
      const cid = await ensureSaved();
      await api.post(`/email/campaigns/${cid}/schedule`, { scheduled_at: new Date(scheduleAt).toISOString() });
      toast.success("Campaign scheduled");
      navigate(`/email/campaigns/${cid}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Schedule failed"); }
  };

  const applyTemplate = (t) => {
    setCampaign({
      ...campaign,
      subject: campaign.subject || t.subject,
      preview_text: campaign.preview_text || t.preview_text || "",
      body_html: t.body_html,
    });
    setShowTemplatePicker(false);
    toast.success(`Loaded "${t.name}"`);
  };

  const audienceSummary = useMemo(() => {
    const a = campaign.audience;
    const parts = [];
    if (a.list_ids?.length) parts.push(`${a.list_ids.length} list(s)`);
    if (a.brands?.length) parts.push(`brand: ${a.brands.join(", ")}`);
    if (a.inquiry_types?.length) parts.push(`type: ${a.inquiry_types.join(", ")}`);
    if (a.statuses?.length) parts.push(`status: ${a.statuses.join(", ")}`);
    if (a.tags?.length) parts.push(`tag: ${a.tags.join(", ")}`);
    return parts.length ? parts.join(" · ") : "No filters — all subscribed contacts";
  }, [campaign.audience]);

  const togglePill = (field, value) => {
    const arr = campaign.audience[field] || [];
    setCampaign({
      ...campaign,
      audience: { ...campaign.audience, [field]: arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value] },
    });
  };

  return (
    <div data-testid="campaign-builder-page">
      <EmailNav />
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <h2 className="text-lg font-semibold">{id ? "Edit Campaign" : "New Campaign"}</h2>
          <p className="text-xs text-muted-foreground">Step {step} of 3</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={saveDraft} data-testid="builder-save-draft"><Save size={14} className="mr-2" /> Save draft</Button>
          <Button variant="outline" size="sm" onClick={() => setShowTestDialog(true)} data-testid="builder-send-test"><Eye size={14} className="mr-2" /> Send test</Button>
        </div>
      </div>

      {/* Step 1: Basics & Audience */}
      {step === 1 && (
        <Card>
          <CardHeader><CardTitle className="text-base font-outfit">1. Campaign basics & audience</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Campaign name (internal)</label>
                <Input data-testid="builder-name" placeholder="e.g. Spring 2026 Field Rental Push" value={campaign.name} onChange={(e) => setCampaign({ ...campaign, name: e.target.value })} />
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Brand</label>
                <Select value={campaign.brand} onValueChange={(v) => setCampaign({ ...campaign, brand: v })}>
                  <SelectTrigger data-testid="builder-brand"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="rrdc">Rolling River</SelectItem>
                    <SelectItem value="riverview">Riverview Athletics</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">From name</label>
                <Input value={campaign.from_name} onChange={(e) => setCampaign({ ...campaign, from_name: e.target.value })} />
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Reply-to (optional)</label>
                <Input placeholder="info@rollingriver.com" value={campaign.reply_to} onChange={(e) => setCampaign({ ...campaign, reply_to: e.target.value })} />
              </div>
            </div>

            <div className="border-t border-border pt-4">
              <h3 className="font-semibold text-sm mb-2">Audience</h3>
              <p className="text-xs text-muted-foreground mb-3">Pick lists and/or filters. Filters narrow further. Unsubscribed contacts are excluded by default.</p>

              <div className="mb-4">
                <label className="text-xs text-muted-foreground mb-1 block">Lists ({campaign.audience.list_ids?.length || 0} selected)</label>
                <div className="flex gap-1.5 flex-wrap">
                  {lists.length === 0 && <p className="text-xs text-muted-foreground italic">No lists yet — create one in the Lists tab.</p>}
                  {lists.map((l) => (
                    <button
                      key={l.id}
                      data-testid={`audience-list-${l.id}`}
                      onClick={() => togglePill("list_ids", l.id)}
                      className={`px-3 py-1 rounded-full border text-xs ${campaign.audience.list_ids?.includes(l.id) ? "bg-primary/15 border-primary text-primary" : "border-border text-muted-foreground hover:bg-accent"}`}
                    >
                      {l.name} <span className="opacity-60">({l.count || 0})</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Brand filter</label>
                  <div className="flex gap-1.5 flex-wrap">
                    {["rrdc", "riverview"].map((b) => (
                      <button key={b} data-testid={`audience-brand-${b}`} onClick={() => togglePill("brands", b)}
                        className={`px-3 py-1 rounded-full border text-xs capitalize ${campaign.audience.brands?.includes(b) ? "bg-primary/15 border-primary text-primary" : "border-border text-muted-foreground hover:bg-accent"}`}>{b}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Inquiry type</label>
                  <div className="flex gap-1.5 flex-wrap">
                    {INQUIRY_TYPES.map((t) => (
                      <button key={t} data-testid={`audience-type-${t}`} onClick={() => togglePill("inquiry_types", t)}
                        className={`px-3 py-1 rounded-full border text-xs ${campaign.audience.inquiry_types?.includes(t) ? "bg-primary/15 border-primary text-primary" : "border-border text-muted-foreground hover:bg-accent"}`}>
                        {t.replace(/_/g, " ")}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="md:col-span-2">
                  <label className="text-xs text-muted-foreground mb-1 block">Lead status</label>
                  <div className="flex gap-1.5 flex-wrap">
                    {LEAD_STATUSES.map((s) => (
                      <button key={s} data-testid={`audience-status-${s}`} onClick={() => togglePill("statuses", s)}
                        className={`px-3 py-1 rounded-full border text-xs ${campaign.audience.statuses?.includes(s) ? "bg-primary/15 border-primary text-primary" : "border-border text-muted-foreground hover:bg-accent"}`}>
                        {s.replace(/_/g, " ")}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <p className="text-xs text-muted-foreground italic mt-3">{audienceSummary}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 2: Content */}
      {step === 2 && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base font-outfit">2. Email content</CardTitle>
            <Button variant="outline" size="sm" onClick={() => setShowTemplatePicker(true)} data-testid="builder-pick-template">
              <Sparkles size={13} className="mr-2" /> Pick a template
            </Button>
          </CardHeader>
          <CardContent className="space-y-3">
            <Input data-testid="builder-subject" placeholder="Subject line" value={campaign.subject} onChange={(e) => setCampaign({ ...campaign, subject: e.target.value })} />
            <Input placeholder="Preview text (shown after subject in inbox)" value={campaign.preview_text} onChange={(e) => setCampaign({ ...campaign, preview_text: e.target.value })} />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              <textarea
                data-testid="builder-body"
                className="w-full h-[420px] rounded-md border border-border bg-background p-3 font-mono text-xs"
                placeholder="HTML body. Use {{first_name}} for personalization."
                value={campaign.body_html}
                onChange={(e) => setCampaign({ ...campaign, body_html: e.target.value })}
              />
              <div className="border border-border rounded-md p-3 bg-white text-black h-[420px] overflow-auto">
                <div dangerouslySetInnerHTML={{ __html: (campaign.body_html || "").replace(/\{\{first_name\}\}/g, "Jamie").replace(/\{\{last_name\}\}/g, "Carter").replace(/\{\{email\}\}/g, "jamie@example.com") }} />
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground">Merge tags: <code>{"{{first_name}}"}</code> · <code>{"{{last_name}}"}</code> · <code>{"{{email}}"}</code>. Tracking pixel + unsubscribe footer are added automatically.</p>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Review & Send */}
      {step === 3 && (
        <Card>
          <CardHeader><CardTitle className="text-base font-outfit">3. Review & send</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="rounded-lg border border-border p-4 space-y-2 text-sm">
              <div><span className="text-muted-foreground">Campaign:</span> <strong>{campaign.name || "—"}</strong></div>
              <div><span className="text-muted-foreground">Subject:</span> {campaign.subject || "—"}</div>
              {campaign.preview_text && <div><span className="text-muted-foreground">Preview:</span> {campaign.preview_text}</div>}
              <div><span className="text-muted-foreground">Brand:</span> <Badge variant="outline" className="capitalize">{campaign.brand}</Badge></div>
              <div><span className="text-muted-foreground">Audience:</span> {audienceSummary}</div>
            </div>

            {audiencePreview ? (
              <div className="rounded-lg bg-primary/5 border border-primary/30 p-4">
                <div className="text-2xl font-bold">{audiencePreview.total}</div>
                <div className="text-xs text-muted-foreground">subscribed contacts will receive this</div>
                {audiencePreview.sample?.length > 0 && (
                  <div className="text-xs text-muted-foreground mt-2 line-clamp-2">
                    Sample: {audiencePreview.sample.slice(0, 6).map((c) => c.email).join(", ")}
                    {audiencePreview.total > 6 && ` … and ${audiencePreview.total - 6} more`}
                  </div>
                )}
              </div>
            ) : (
              <Button variant="outline" onClick={previewAudience} data-testid="builder-preview-audience">
                <Eye size={14} className="mr-2" /> Preview audience size
              </Button>
            )}

            <div className="flex gap-2 flex-wrap">
              <Button onClick={sendNow} data-testid="builder-send-now">
                <Send size={14} className="mr-2" /> Send Now
              </Button>
              <Button variant="outline" onClick={() => setShowSchedule(true)} data-testid="builder-schedule">
                <CalIcon size={14} className="mr-2" /> Schedule
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="flex justify-between mt-6">
        <Button variant="outline" disabled={step === 1} onClick={() => setStep(step - 1)} data-testid="builder-prev">
          <ChevronLeft size={14} className="mr-1" /> Back
        </Button>
        {step < 3 && (
          <Button onClick={() => setStep(step + 1)} data-testid="builder-next">
            Next <ChevronRight size={14} className="ml-1" />
          </Button>
        )}
      </div>

      {/* Test send dialog */}
      <Dialog open={showTestDialog} onOpenChange={setShowTestDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Send test email</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">Sends one copy of this email to the address below for preview. Tracking + unsubscribe footer included.</p>
          <Input data-testid="test-email-input" placeholder="you@example.com" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTestDialog(false)}>Cancel</Button>
            <Button onClick={sendTest} data-testid="test-email-send">Send test</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Schedule dialog */}
      <Dialog open={showSchedule} onOpenChange={setShowSchedule}>
        <DialogContent>
          <DialogHeader><DialogTitle>Schedule campaign</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">Pick a date and time (your browser's local time).</p>
          <Input data-testid="schedule-input" type="datetime-local" value={scheduleAt} onChange={(e) => setScheduleAt(e.target.value)} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSchedule(false)}>Cancel</Button>
            <Button onClick={schedule} data-testid="schedule-confirm">Schedule</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Template picker */}
      <Dialog open={showTemplatePicker} onOpenChange={setShowTemplatePicker}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Pick a template</DialogTitle></DialogHeader>
          {templates.length === 0 && <p className="text-sm text-muted-foreground py-8 text-center">No templates saved. Create one in the Templates tab first.</p>}
          <div className="space-y-2">
            {templates.map((t) => (
              <button
                key={t.id}
                data-testid={`pick-template-${t.id}`}
                onClick={() => applyTemplate(t)}
                className="w-full text-left p-3 rounded-lg border border-border hover:bg-accent transition-colors"
              >
                <div className="flex items-center justify-between gap-2">
                  <h4 className="font-semibold text-sm">{t.name}</h4>
                  <Badge variant="outline" className="text-[10px] capitalize">{t.category}</Badge>
                </div>
                <p className="text-xs text-muted-foreground mt-1">{t.subject}</p>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
